<template>
  <div class="boost-pagination">
    <div
      v-if="totalPages > 2"
      class="boost-pagination__inner"
    >
      <button
        v-if="totalPages > 5"
        class="boost-pagination__previous"
        :class="{ [$class('disabled')]: page === 1 }"
        modifiers="nav-arrow"
        type="button"
        @click="handleButtonClick('previous')"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.carousel.previous')"
        />
        <icon-arrow-left />
      </button>

      <div
        v-for="index of pagesWithOffset"
        :key="index"
        class="boost-pagination__page"
        :class="{ [$class('active')]: index === page }"
        :data-id="index"
        @click="handlePageChange(index)"
        v-text="index"
      />

      <button
        v-if="totalPages > 5"
        class="boost-pagination__next"
        :class="{ [$class('disabled')]: page === totalPages - 1 }"
        modifiers="nav-arrow"
        type="button"
        @click="handleButtonClick('next')"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.carousel.next')"
        />
        <icon-arrow-right />
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Boost pagination (main-collection)
 * -----------------------------------------------------------------------------
 * Pagination component for Boost collection and search templates.
 *
 * @param {Number} limit - Product limit per page.
 * @param {Number} page - Current page.
 * @param {Number} totalProduct - Total number of products.
 *
 */
import IconArrowLeft from '~icons/direction/arrow/left.svg'
import IconArrowRight from '~icons/direction/arrow/right.svg'

export default {
  name: 'BoostPagination',

  components: {
    IconArrowLeft,
    IconArrowRight,
  },

  props: {
    limit: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    totalProduct: {
      type: Number,
      default: 0,
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'object',
  },

  computed: {

    /**
     * Compute total pages.
     * @returns {Number}
     */
    totalPages() {
      return Math.ceil(this.totalProduct / this.limit) + 1
    },

    /**
     * Compute pages with offset.
     * @returns {Number}
     */
    pagesWithOffset() {
      const pageArray = [...Array(this.totalPages).keys()]
      let offset = 0

      pageArray.shift()

      if (this.page > 5) {
        offset = this.page - 5
      }

      return pageArray.splice(offset, 5)
    },
  },

  methods: {

    /**
     * Handle pagination click.
     * @param {Number} index - Index of page to change to.
     */
    handlePageChange(index) {
      if (index === this.page) {
        return
      }

      const requestParam = `&page=${index}`
      const action = 'add'

      const data = {
        requestParam,
        action,
      }

      this.$emit('choices-updated', data)

      /**
       * Disable smooth scroll if prefers reduced motion.
       */
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth'

      window.scroll({
        top: 0,
        left: 0,
        behavior,
      })
    },

    /**
     * Update page on pagination button click.
     * @param {String} type - Type of button, `previous` or `next`.
     */
    handleButtonClick(type) {
      if (type === 'next') {
        this.handlePageChange(this.page + 1)
        return
      }

      this.handlePageChange(this.page - 1)
    },
  },
}
</script>

<style lang="scss">
@import './boost-pagination';
</style>
